import React, { useState } from 'react';
import { Category } from '../types/category';
import CategoryTree from './CategoryTree';
import { useCategories } from '../hooks/useCategories';
import { ExclamationCircleFilled } from '@ant-design/icons';
import {ROOT_LEVEL} from "../utils/constants";

const getCategoryPathIds = (categories: Category[], categoryId: number | null): string[] => {
  if (!categoryId) return [];
  
  const result: string[] = [];
  let currentId: number | null = categoryId;
  
  while (currentId !== null) {
    result.push(currentId.toString());
    const category = categories.find(c => c.id === currentId);
    currentId = category?.parent_id || null;
  }
  
  return result;
};

interface CategoryTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (categoryId: number) => void;
  selectedCategoryId: number;
}

export default function CategoryTreeModal({
  isOpen,
  onClose,
  onSelect,
  selectedCategoryId
}: CategoryTreeModalProps) {
  const [selected, setSelected] = useState<number>(ROOT_LEVEL.id);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const { categories } = useCategories();

  if (!isOpen) return null;

  const reinitAndClose = () => {
    setSelected(ROOT_LEVEL.id);
    onClose();
  };

  const getSourceCategory = () => {
    if (!selectedCategoryId) return null;
    return categories?.find(c => c.id === selectedCategoryId);
  };

  const getTargetCategory = () => {
    return categories?.find(c => c.id === selected);
  };

  const getSubcategoriesCount = (categoryId: number | null) => {
    if (!categoryId || !categories) return 0;
    return categories.filter(c => c.parent_id === categoryId).length;
  };


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">

      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="relative z-50 inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-[#0b1d51] shadow-xl rounded-2xl">
          <h3 className="text-lg font-medium mb-4">
            Move Category: <span className="text-[#048ba8] font-semibold">{selectedCategoryId ? categories?.find(c => c.id === selectedCategoryId)?.name : ''}</span>
          </h3>
          <div className="space-y-4">
            <p className="text-[#0b1d51] mb-2">Select a category to put your category into:</p>
            <div className="min-h-[200px] max-h-96 overflow-y-auto border border-[#048ba8] rounded-lg p-3">
              <CategoryTree
                onSelectCategory={setSelected}
                showActions={false}
                isMoveCategoryModal={true}
                selectedCategoryId={selected}
                filterOutCategory={selectedCategoryId}
              />
            </div>
            
            <div className="p-3 bg-[#f4d35e] bg-opacity-20 rounded-lg">
              <p className="text-sm text-[#0b1d51] dark:text-white">
                <strong>Note:</strong> Moving this note will change its category and it will appear
                under the selected category in the note list.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-[#849469] hover:text-[#849469]/90 focus:outline-none"
              onClick={reinitAndClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-[#048ba8] hover:bg-[#048ba8]/90 rounded-2xl focus:outline-none"
              onClick={() => setIsConfirmModalVisible(true)}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      <div className={isConfirmModalVisible ? "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" : "hidden"}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-fade-in">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-[#f4d35e] flex items-center justify-center">
              <ExclamationCircleFilled className="text-xl" />
            </div>
            <h3 className="text-xl font-semibold">Move Category</h3>
          </div>
          
          <div className="mb-6">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-3">Category Details:</h4>
                <div className="ml-4 space-y-2 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#048ba8]" />
                  <p className="pl-4">
                    <span className="text-[#849469]">Name:</span>{' '}
                    <span className="font-bold">{getSourceCategory()?.name || 'Root'}</span>
                  </p>
                  <p className="pl-4">
                    <span className="text-[#849469]">Subcategories:</span>{' '}
                    <span className="font-bold">{(() => {
                      const sourceCategory = getSourceCategory();
                      return sourceCategory ? getSubcategoriesCount(sourceCategory.id) : 0;
                    })()}</span>
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-3">New Location:</h4>
                <div className="ml-4 space-y-2 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#048ba8]" />
                  <p className="pl-4">
                    <span className="text-[#849469]">Parent Category:</span>{' '}
                    <span className="font-bold">{getTargetCategory()?.name || 'Root'}</span>
                  </p>
                  <p className="pl-4">
                    <span className="text-[#849469]">Existing Subcategories:</span>{' '}
                    <span className="font-bold">{(() => {
                      const targetCategory = getTargetCategory();
                      return targetCategory ? getSubcategoriesCount(targetCategory.id) : 0;
                    })()}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-[#f4d35e] bg-opacity-10 rounded-xl p-4 mb-6">
            <p className="text-sm text-[#0b1d51]">
              <span className="font-medium">Note:</span> Moving this category will also move all its notes and subcategories. This action will maintain the category structure and all contained items.
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsConfirmModalVisible(false)}
              className="px-5 py-2 text-sm font-medium text-[#849469] hover:text-[#849469]/90 focus:outline-none rounded-2xl border border-[#849469]"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSelect(selected);
                reinitAndClose();
                setIsConfirmModalVisible(false);
              }}
              className="px-5 py-2 text-sm font-medium text-white bg-[#d64933] hover:bg-[#d64933]/90 rounded-2xl focus:outline-none"
            >
              Move
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
